/**
 * In-tab event emitter bridged to other tabs through BroadcastChannel.
 * The mock repositories publish `changed` after every committed write, which
 * is exactly what Supabase Realtime will publish in production — so the UI's
 * subscription code does not change when the backend arrives.
 */
export type BusMessage =
  | { type: 'changed'; tables: string[]; entityIds?: string[]; sourceTab: string }
  | { type: 'presence'; entityId: string; accountId: string; displayName: string; editing: boolean; sourceTab: string; at: number }
  | { type: 'simulation'; sourceTab: string }
  | { type: 'reset'; sourceTab: string };

/** Distributive Omit so each variant of the union keeps its own fields. */
type DistOmit<T, K extends PropertyKey> = T extends unknown ? Omit<T, K> : never;
export type BusMessageInput = DistOmit<BusMessage, 'sourceTab'>;

type Listener = (m: BusMessage) => void;

export const TAB_ID = Math.random().toString(36).slice(2, 10);

const listeners = new Set<Listener>();
let channel: BroadcastChannel | null = null;

try {
  if (typeof BroadcastChannel !== 'undefined') {
    channel = new BroadcastChannel('edry-family-sync');
    channel.onmessage = (ev: MessageEvent<BusMessage>) => {
      const m = ev.data;
      if (!m || m.sourceTab === TAB_ID) return;
      for (const l of listeners) l(m);
    };
  }
} catch {
  channel = null;
}

export function subscribe(l: Listener): () => void {
  listeners.add(l);
  return () => listeners.delete(l);
}

/** Emit locally and to other tabs. */
export function emit(m: BusMessageInput): void {
  const full = { ...m, sourceTab: TAB_ID } as BusMessage;
  for (const l of listeners) l(full);
  try {
    channel?.postMessage(full);
  } catch {
    /* ignore */
  }
}

/** Emit only inside this tab (used after the local store has absorbed a remote change). */
export function emitLocal(m: BusMessageInput): void {
  const full = { ...m, sourceTab: TAB_ID } as BusMessage;
  for (const l of listeners) l(full);
}

export function emitChanged(tables: string[], entityIds?: string[]): void {
  emit({ type: 'changed', tables, entityIds });
}

export const isLocal = (m: BusMessage): boolean => m.sourceTab === TAB_ID;
